/**
 * Preferences locales. Rien ne part sur le reseau, sauf le pseudo au join.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { detectLanguage, isLanguage, type Language } from '../locales/index.js';
import { randomFreeAvatar } from '../account/avatars.js';
import { randomNickname } from '@retusch/shared';

interface UiState {
  nickname: string;
  language: Language;
  /**
   * Le joueur a-t-il choisi sa langue explicitement ? Tant que non, on suit le
   * navigateur : y compris si celui-ci change entre deux visites.
   */
  languageChosen: boolean;
  muted: boolean;
  /**
   * Avatar choisi. Reservee aux comptes, mais stockee ici avec le pseudo : ce
   * sont les deux memes preferences, et le compte ne fera que les synchroniser
   * entre appareils.
   */
  avatar: string | null;
  setNickname(nickname: string): void;
  setAvatar(avatar: string | null): void;
  /**
   * Attribue un avatar gratuit a qui n'en a pas. Tire UNE fois puis conserve :
   * un avatar qui change a chaque visite ne distingue plus personne, et les
   * autres joueurs le verraient sauter d'une partie a l'autre.
   */
  ensureAvatar(): void;
  /**
   * Garantit un pseudo. Celui deja saisi est conserve tel quel ; s'il a ete
   * efface volontairement, on en tire un. La creation de compte ne le demande
   * plus, elle ne peut donc pas se contenter d'un champ vide.
   */
  ensureNickname(): void;
  setLanguage(language: Language): void;
  toggleMuted(): void;
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      nickname: '',
      language: detectLanguage(),
      languageChosen: false,
      muted: false,
      avatar: null,
      setNickname: (nickname) => set({ nickname }),
      setAvatar: (avatar) => set({ avatar }),
      ensureAvatar: () =>
        set((state) => (state.avatar === null ? { avatar: randomFreeAvatar() } : {})),
      ensureNickname: () =>
        set((state) => (state.nickname.trim() === '' ? { nickname: randomNickname() } : {})),
      setLanguage: (language) => set({ language, languageChosen: true }),
      toggleMuted: () => set((state) => ({ muted: !state.muted })),
    }),
    {
      // localStorage : ces preferences survivent a la fermeture de l'onglet,
      // contrairement au token de session.
      name: 'retusch:ui',
      partialize: (state) => ({
        nickname: state.nickname,
        avatar: state.avatar,
        language: state.language,
        languageChosen: state.languageChosen,
        muted: state.muted,
      }),
      merge: (persisted, current) => {
        const stored = persisted as Partial<UiState> | undefined;
        const chosen = stored?.languageChosen === true;
        return {
          ...current,
          ...stored,
          // Sans choix explicite, la detection navigateur reprend la main a
          // chaque visite plutot que de figer la langue du premier passage.
          language: chosen && isLanguage(stored?.language) ? stored.language : detectLanguage(),
          languageChosen: chosen,
        };
      },
    },
  ),
);

/** Tient l'attribut lang du document aligne sur la langue choisie. */
export function syncDocumentLanguage(): () => void {
  const apply = (language: Language): void => {
    document.documentElement.lang = language;
  };
  apply(useUiStore.getState().language);
  return useUiStore.subscribe((state) => apply(state.language));
}
